import axios from 'axios';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { Gizmo } from '../../gizmos';
import { IGizmo, IGizmoData, IUserV2 } from '../../types';
import { downloadFileS3 } from './utils';
import CommonBinder from '../common-binder';

interface ISignatureFieldBinder {
  init(): Promise<void>;
}

class SignatureFieldBinder extends CommonBinder
  implements ISignatureFieldBinder {
  private _files: File[];
  public files: Subject<File[]>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this._files = [];
    this.files = new Subject<File[]>();
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const data = this._gizmo.getData();

      if (data && !Array.isArray(data) && apiUrl && user) {
        // this._downloadFileV2(data).then(() => {});
        this._downloadFileS3({ data }).then(file => {
          if (file) {
            this._files = [file];
            this.files.next(this._files);
          }
        });
      }

      resolve();
    });
  };

  public setFiles = (files: File[]): Promise<void> => {
    return new Promise<void>(async resolve => {
      const platformInterface = this._gizmo
        .getFoundation()
        ?.getPlatformInterface();

      // Right now we're going to deal with only one file
      if (files.length === 1) {
        this._files = files;
        this.files.next(this._files);
        const file = files[0];
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();

        // const attachmentId = uuid();
        let attachmentId = uuid();
        const f: any = file;
        // This field will happen when the platform layer is mobile. This happens because of the
        // file library, we have to store the file before sending it to the core
        if (f.attachmentId) {
          attachmentId = f.attachmentId;
        }

        if (platformInterface && fileExtension) {
          platformInterface.fileSet({
            path: '',
            fileFid: attachmentId,
            fileName,
            fileExtension,
            file,
          });
        }

        await this._gizmo.setData({
          v: attachmentId,
          fd: {
            attachmentId,
            fileName,
            fileExtension,
            fileSize: file.size,
            fileType: file.type,
          },
        });
      } else if (files.length === 0 && this._files.length === 1) {
        // If we're trying to assign an empty list and we have a file present,
        // we're going to remove if from the platform interface and clear the
        // gizmo data
        const data = this._gizmo.getData();

        if (
          platformInterface &&
          !Array.isArray(data) &&
          data?.fd?.attachmentId
        ) {
          this._files = [];
          this.files.next(this._files);

          await platformInterface.fileRemove({
            path: '',
            fileFid: data.fd.attachmentId,
            fileExtension: data.fd.fileExtension || 'jpeg',
          });
          await this._gizmo.setData(undefined);
        }
      }

      resolve();
    });
  };

  /**
   * Get files stored in memory
   */
  public getFiles = (): File[] => {
    return this._files;
  };

  private _downloadFileS3 = async ({
    data,
    fid,
    formId,
    filledFormId,
  }: IDownloadFileV2): Promise<File | undefined> => {
    return await downloadFileS3({
      data,
      fid,
      formId,
      filledFormId,
      context: {
        gizmo: this._gizmo,
      },
    });
  };

  public _downloadFileV2 = (data: IGizmoData): Promise<void> => {
    return new Promise<void>(async resolve => {
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const callbacks = this._gizmo.getCallbacks();
      const config: IGizmo = this._gizmo.getConfig();

      const formId = callbacks.getInfoCB({ type: 'rootGizmoFid' });
      const filledFormId = callbacks.getInfoCB({ type: 'rootGizmoDataFid' });
      const fId = config.fid;
      const answerIndex = '0';
      const attachmentId = data.fd?.attachmentId;
      const extension = data.fd?.fileExtension || 'jpg';

      if (apiUrl && user) {
        const credentials: any = user.credentials;
        let url = `${apiUrl}/v3/forms/${formId}/filled_forms_attachment?`;
        url += `attachmentId=${attachmentId}&formId=${formId}&filledFormId=${filledFormId}&`;
        url += `fId=${fId}&answerIndex=${answerIndex}&extension=${extension}`;

        try {
          const response = await axios({
            method: 'GET',
            url,
            responseType: 'blob',
            headers: {
              ...credentials,
            },
          });

          if (response.status === 200 || response.status === 201) {
            const file = new File(
              [response.data],
              `${attachmentId}.${extension}`,
              {
                type: 'image/jpeg',
              }
            );

            this._files = [file];
            this.files.next(this._files);
          }
        } catch (e) {}
      }

      resolve();
    });
  };
}

interface IDownloadFileV2 {
  data: IGizmoData;
  fid?: string;
  formId?: string;
  filledFormId?: string;
}

export default SignatureFieldBinder;
